


export type BiosenseSeverity = 'error' | 'warning';

export interface BiosenseErrorInfo {
  code: number;
  name: string;
  cause: string;
  solution: string;
  severity: BiosenseSeverity;
}

export const BIOSENSE_ERROR_MAP: Record<number, BiosenseErrorInfo> = {
  // ───────── DEVICE ERRORS ─────────
  8: {
    code: 8,
    name: 'DEVICE_CODE_MINIMUM_OS_VERSION_ERROR',
    cause:
      "The device's operating system version is lower than the minimum version required to run the SDK.",
    solution:
      'Upgrade the device operating system or use another device.',
    severity: 'error',
  },
  17: {
    code: 17,
    name: 'DEVICE_CODE_CLOCK_SKEW_ERROR',
    cause: 'Severe clock skew detected.',
    solution:
      "The user should verify that the device date, time and timezone are set correctly. It is recommended to enable 'Set automatically' in the device settings.",
    severity: 'error',
  },
  18: {
    code: 18,
    name: 'DEVICE_CODE_MINIMUM_BROWSER_VERSION_ERROR',
    cause: 'The browser version is older than the minimum required version.',
    solution:
      "Upgrade the device's browser or use another browser.",
    severity: 'error',
  },

  // ───────── CAMERA ERRORS / WARNINGS ─────────
  1001: {
    code: 1001,
    name: 'CAMERA_CODE_NO_CAMERA_ERROR',
    cause:
      'The session.start() command was called, but the device has no camera with the required specifications.',
    solution:
      'The camera must support a resolution of 640x480 at 30FPS. The user should verify that the device camera works properly.',
    severity: 'error',
  },
  1002: {
    code: 1002,
    name: 'CAMERA_CODE_CAMERA_OPEN_ERROR',
    cause: 'Could not operate the camera.',
    solution:
      'The user should verify that the device camera works properly and try again. If the problem persists, the user should restart the application.',
    severity: 'error',
  },
  1005: {
    code: 1005,
    name: 'CAMERA_CODE_CAMERA_MISSING_PERMISSIONS_ERROR',
    cause:
      'The application does not have permission to access the camera.',
    solution:
      'The user should grant the web page permission to use the camera.',
    severity: 'error',
  },
  1501: {
    code: 1501,
    name: 'CAMERA_CODE_UNEXPECTED_IMAGE_DIMENSIONS_WARNING',
    cause:
      'The images received from the camera have a different resolution than the requested resolution.',
    solution:
      'Instruct the user to retry taking the measurements. If the problem persists, then the device is probably malfunctioning, or does not support VGA resolution. The user should be instructed to use a different device. Although this is just a "warning" alert, and the measurement continues, the accuracy of the measurement may be impacted by the unexpected resolution.',
    severity: 'warning',
  },

  // ───────── LICENSE ERRORS ─────────
  2002: {
    code: 2002,
    name: 'LICENSE_CODE_ACTIVATION_LIMIT_REACHED_ERROR',
    cause: 'No more devices can be used with your license.',
    solution:
      'Contact the sales team to increase the number of device authorizations in your license.',
    severity: 'error',
  },
  2003: {
    code: 2003,
    name: 'LICENSE_CODE_METER_ATTRIBUTE_USES_LIMIT_REACHED_ERROR',
    cause: 'No more measurements are allowed for the provided license.',
    solution:
      'Contact the sales team to increase the number of measurements in your license.',
    severity: 'error',
  },
  2004: {
    code: 2004,
    name: 'LICENSE_CODE_AUTHENTICATION_FAILED_ERROR',
    cause:
      'Several issues might cause this error: clock skew detected, the SDK was unable to authenticate the license, a bad token was received from the license server.',
    solution:
      'The user should take the following actions: Check the internet connection. Set the device time correctly. Verify that the device has sufficient storage.',
    severity: 'error',
  },
  2007: {
    code: 2007,
    name: 'LICENSE_CODE_INVALID_LICENSE_KEY_ERROR',
    cause: 'The provided license key is invalid.',
    solution:
      'Use the license key provided by the support team. If the problem persists, contact the support team.',
    severity: 'error',
  },
  2010: {
    code: 2010,
    name: 'LICENSE_CODE_REVOKED_LICENSE_ERROR',
    cause: 'The license was revoked.',
    solution: 'Contact the customer support team.',
    severity: 'error',
  },
  2016: {
    code: 2016,
    name: 'LICENSE_CODE_INTERNAL_ERROR_9',
    cause:
      'SSL error. Unable to authenticate the license server response.',
    solution:
      "The user should check the device's local time, internet connection, try a different network, or try again later.",
    severity: 'error',
  },
  2017: {
    code: 2017,
    name: 'LICENSE_CODE_LICENSE_EXPIRED_ERROR',
    cause: 'The license has expired.',
    solution: 'Contact the customer support team.',
    severity: 'error',
  },
  2018: {
    code: 2018,
    name: 'LICENSE_CODE_LICENSE_SUSPENDED_ERROR',
    cause: 'The license is suspended.',
    solution: 'Contact the customer support team.',
    severity: 'error',
  },
  2024: {
    code: 2024,
    name: 'LICENSE_CODE_NETWORK_ISSUES_ERROR',
    cause: 'No internet connection.',
    solution:
      'The user should check the internet connection and try again.',
    severity: 'error',
  },
  2025: {
    code: 2025,
    name: 'LICENSE_CODE_SSL_HANDSHAKE_ERROR',
    cause: 'SSL certificate security warning.',
    solution:
      "The user should check the device's local time, internet connection, try a different network, or try again later.",
    severity: 'error',
  },
  2032: {
    code: 2032,
    name: 'LICENSE_CODE_INPUT_LICENSE_KEY_EMPTY_ERROR',
    cause: 'No license key was provided to the SDK.',
    solution:
      'Initiate the SDK with a valid license key, provided by the support team.',
    severity: 'error',
  },
  2034: {
    code: 2034,
    name: 'LICENSE_CODE_INPUT_PRODUCT_ID_ILLEGAL_ERROR',
    cause: 'An invalid product ID was provided.',
    solution: 'The product ID must be null when establishing a session.',
    severity: 'error',
  },
  2035: {
    code: 2035,
    name: 'LICENSE_CODE_CANNOT_OPEN_FILE_FOR_READ_ERROR',
    cause:
      'The SDK cannot read from the file system, or a file may be corrupted.',
    solution: 'Instruct the user to check the installation integrity.',
    severity: 'error',
  },
  2036: {
    code: 2036,
    name: 'LICENSE_CODE_MONTHLY_USAGE_TRACKING_REQUIRES_SYNC_ERROR',
    cause:
      'The SDK failed to authenticate with the license server as required by the license type.',
    solution:
      'Instruct the user to check the internet connection and try again.',
    severity: 'error',
  },
  2037: {
    code: 2037,
    name: 'LICENSE_CODE_SSL_HANDSHAKE_DEVICE_DATE_ERROR',
    cause: 'SSL certificate security warning.',
    solution:
      "The user should check the device's local time, internet connection, try a different network, or try again later.",
    severity: 'error',
  },
  2038: {
    code: 2038,
    name: 'LICENSE_CODE_SSL_HANDSHAKE_CERTIFICATE_EXPIRED_ERROR',
    cause: 'SSL certificate security warning.',
    solution:
      "The user should check the device's local time, internet connection, try a different network, or try again later.",
    severity: 'error',
  },
  2039: {
    code: 2039,
    name: 'LICENSE_CODE_MIN_SDK_ERROR',
    cause: 'The SDK version is too old to be used with this license.',
    solution:
      'Upgrade to the latest SDK version or contact the support team.',
    severity: 'error',
  },
  2042: {
    code: 2042,
    name: 'LICENSE_CODE_NETWORK_TIMEOUT_ERROR',
    cause: 'Network timeout reached for a single call.',
    solution:
      'Advise the user to verify the internet connection speed, restart the application, wait briefly, attempt using another network, or try again later. If the issue persists, contact the support team.',
    severity: 'error',
  },

  // ───────── MEASUREMENT ERRORS / WARNINGS ─────────
  3003: {
    code: 3003,
    name: 'MEASUREMENT_CODE_MISDETECTION_DURATION_EXCEEDS_LIMIT_ERROR',
    cause:
      'The face was not detected for a period of over 0.5 seconds several times.',
    solution:
      'The user should ensure that the face remains still during the measurement and follow the best practices provided by the provider for taking accurate measurements.',
    severity: 'error',
  },
  3004: {
    code: 3004,
    name: 'MEASUREMENT_CODE_INVALID_RECENT_DETECTION_RATE_ERROR',
    cause:
      "More than two periods of multiple frame losses were detected during the measurement. This issue may occur if the device is overloaded and unable to process video frames in real-time, or if there is insufficient lighting on the user's face.",
    solution:
      'For the User: Ensure the device is not overloaded, overheated, or running low on resources; improve lighting; close other applications; or restart the device. For the Application Developer: Avoid using debugging tools or intensive logging during the measurement.',
    severity: 'error',
  },
  3006: {
    code: 3006,
    name: 'MEASUREMENT_CODE_LICENSE_ACTIVATION_FAILED_ERROR',
    cause: 'The license activation failed.',
    solution:
      "The user should check the device's internet connection, and should check that no invalid proxy configuration is used. If the problem persists, the user should contact the support team.",
    severity: 'error',
  },
  3008: {
    code: 3008,
    name: 'MEASUREMENT_CODE_INVALID_MEASUREMENT_AVERAGE_DETECTION_RATE_ERROR',
    cause:
      "The average frame rate is significantly lower than expected. This issue may result from device overloading or insufficient lighting on the user's face.",
    solution:
      'For the User: Close resource-intensive applications, ensure the device is not busy, overheated, or overloaded, allow the device to cool down, and retry. If the problem persists, consider using another device. For the Application Developer: Ensure the SDK implementation does not impose excessive demands on the device and that background activity is minimized.',
    severity: 'error',
  },
  3009: {
    code: 3009,
    name: 'MEASUREMENT_CODE_TOO_MANY_FRAMES_INORDER_ERROR',
    cause:
      'Multiple consecutive frames were received in incorrect timestamp order. This error occurs if warning 3506 is issued multiple times.',
    solution: 'The user should rerun the measurement.',
    severity: 'error',
  },
  3500: {
    code: 3500,
    name: 'MEASUREMENT_CODE_MISDETECTION_DURATION_EXCEEDS_LIMIT_WARNING',
    cause:
      'A face was not detected for a period of over 0.5 seconds.',
    solution:
      'The instantaneous vital signs should not be displayed to the user for a few seconds until the algorithms overcome the detection issues. The user must follow the best practices for taking a measurement.',
    severity: 'warning',
  },
  3505: {
    code: 3505,
    name: 'MEASUREMENT_CODE_INVALID_RECENT_FPS_RATE_WARNING',
    cause:
      'Camera FPS is degraded and may affect the measurement quality.',
    solution:
      'The user should make sure to follow the best practices for taking a measurement.',
    severity: 'warning',
  },
  3506: {
    code: 3506,
    name: 'MEASUREMENT_CODE_MEASUREMENT_MISPLACED_FRAME_WARNING',
    cause:
      'A frame was received in incorrect timestamp order.',
    solution: 'The user should proceed with the measurement.',
    severity: 'warning',
  },

  // ───────── VITAL SIGN WARNINGS ─────────
  4505: {
    code: 4505,
    name: 'VITAL_SIGN_CODE_BLOOD_PRESSURE_PROCESSING_FAILED_WARNING',
    cause:
      'Failure to calculate blood pressure in this measurement. This warning does not impact other vital sign measurements. The failure may have been caused by corrupted installation files.',
    solution:
      'Blood pressure will not be calculated for this measurement session. Other vital sign results will be presented. The user can retry the measurement. If the problem persists, the user should reinstall/repair the app.',
    severity: 'warning',
  },
  4506: {
    code: 4506,
    name: 'VITAL_SIGN_CODE_MEASURING_WITH_NO_ENABLED_VITAL_SIGNS_WARNING',
    cause:
      'No vital signs were processed as part of this measurement.',
    solution:
      'This warning is issued when the license does not support the calculation of any vital signs, or when the SDK cannot access license information. The user should check the internet connection and try another measurement.',
    severity: 'warning',
  },

  // ───────── SESSION ERRORS ─────────
  6004: {
    code: 6004,
    name: 'SESSION_CODE_ILLEGAL_START_CALL_ERROR',
    cause:
      'A session start call was made when the session is not in READY state.',
    solution:
      'Wait for session state to become READY before calling start.',
    severity: 'error',
  },
  6005: {
    code: 6005,
    name: 'SESSION_CODE_ILLEGAL_STOP_CALL_ERROR',
    cause:
      'A session stop call was made when the session is not in PROCESSING state.',
    solution:
      'Call stop only when session is processing.',
    severity: 'error',
  },

  // ───────── INITIALIZATION ERRORS / WARNINGS ─────────
  7002: {
    code: 7002,
    name: 'INITIALIZATION_CODE_INVALID_PROCESSING_TIME_ERROR',
    cause:
      'An invalid session time was provided when creating a session.',
    solution:
      'Use a valid session time. The valid session time range is between 20–180 seconds.',
    severity: 'error',
  },
  7005: {
    code: 7005,
    name: 'INITIALIZATION_CODE_INVALID_LICENSE_FORMAT',
    cause:
      'The provided license key is either empty or its format is invalid.',
    solution:
      'Check the SDK license key provided by the support team. Ensure it is not empty and follows a valid format – avoid including spaces, newlines, or special characters.',
    severity: 'error',
  },
  7006: {
    code: 7006,
    name: 'INITIALIZATION_CODE_SDK_LOAD_FAILURE',
    cause:
      'The SDK fails to load the algorithmic binary file (a.wasm.gz).',
    solution:
      'Make sure a.wasm.gz is whitelisted and is deliverable by the server.',
    severity: 'error',
  },
  7007: {
    code: 7007,
    name: 'INITIALIZATION_CODE_UNSUPPORTED_USER_WEIGHT',
    cause:
      'The weight submitted by the user is not supported. The supported weight range is between 40 to 200 kilograms.',
    solution:
      'The user should either submit a weight within the supported range, or not specify a weight at all.',
    severity: 'error',
  },
  7008: {
    code: 7008,
    name: 'INITIALIZATION_CODE_UNSUPPORTED_USER_AGE',
    cause:
      'The age submitted by the user is not supported. The supported age range is between 18 to 110 years.',
    solution:
      'The user should either submit an age within the supported range or not specify an age at all.',
    severity: 'error',
  },
  7009: {
    code: 7009,
    name: 'INITIALIZATION_CODE_CONCURRENT_SESSIONS_ERROR',
    cause:
      'Trying to create a new session before terminating the previous session.',
    solution:
      'The previous session should be terminated before establishing a new one.',
    severity: 'error',
  },
  7012: {
    code: 7012,
    name: 'INITIALIZATION_CODE_UNSUPPORTED_USER_HEIGHT',
    cause:
      'The height submitted by the user is not supported. The supported height range is between 130 to 230 centimeters.',
    solution:
      'The application should specify a height value within the supported range or not specify a height at all.',
    severity: 'error',
  },
  7013: {
    code: 7013,
    name: 'INITIALIZATION_CODE_MEMORY_ALLOCATION_ERROR',
    cause:
      'The memory allocation failed due to an internal bug in Emscripten when running on iOS 17 or earlier with several invocations of the SDK in the same tab.',
    solution:
      'Initiate each measurement in a new tab. If this issue arises, advise the user to restart the browser or open the page in a new tab (preserving context) and continue as normal. This Webkit issue has been resolved in iOS 18.',
    severity: 'error',
  },
  7014: {
    code: 7014,
    name: 'INITIALIZATION_CODE_INITIAL_MEMORY_ALLOCATION_ERROR',
    cause: 'Memory allocation failure.',
    solution:
      'Instruct the user to close all other applications and active browser tabs, wait for a few seconds and try again. If the problem still persists it means the device is too weak to run the SDK.',
    severity: 'error',
  },
  7015: {
    code: 7015,
    name:
      'INITIALIZATION_CODE_BROWSER_NOT_SUPPORTING_SHARED_ARRAY_BUFFER_ERROR',
    cause: "The user's browser does not support SharedArrayBuffer.",
    solution:
      "Instruct the user to upgrade the browser's version or use another browser/device.",
    severity: 'error',
  },
  7501: {
    code: 7501,
    name: 'INITIALIZATION_MEMORY_CONSUMPTION_WARNING',
    cause:
      'The application is attempting to invoke the SDK in a tab where it has been invoked before. Due to an internal bug in Emscripten for iOS 17 and below there is a potential memory leak that might prevent running the SDK properly.',
    solution:
      'Recurring SDK invocations within the same tab (e.g., refreshing the page multiple times) can result in a memory leak. To mitigate this, each measurement should be initiated in a new tab. If this issue arises, advise the user to restart the browser or open the page in a new tab (preserving context) and continue as normal. This Webkit issue has been resolved in iOS 18.',
    severity: 'warning',
  },
};

/**
 * Helper to get rich error info by code.
 */
export const getBiosenseErrorInfo = (
  code?: number | null,
): BiosenseErrorInfo | null => {
  if (code == null || code === -1) return null;
  return BIOSENSE_ERROR_MAP[code] ?? null;
};